import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '@/components/Layout';
import { FadeIn } from '@/components/Animations';
import { useCommunity } from '@/contexts/CommunityContext';
import { useTranslatedDocumentTitle } from '@/hooks/useDocumentTitle';
import { supabase } from '@/integrations/supabase/client';
import { CommunityPageWithSections } from '@/types/cms';
import { SectionRenderer } from './index';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle } from 'lucide-react';

const CommunityPrivacyPage: React.FC = () => {
  const { currentCommunity } = useCommunity();

  // Set page title
  useTranslatedDocumentTitle('footer.privacy', { 
    community: currentCommunity?.name || 'Community'
  });

  // Fetch page data with sections
  const { data: pageData, isLoading, error } = useQuery({
    queryKey: ['community-page', currentCommunity?.id, 'privacy'],
    queryFn: async () => {
      if (!currentCommunity?.id) throw new Error('No community selected');

      // Fetch page and sections in a single query with joins
      const { data, error } = await supabase
        .from('community_pages')
        .select(`
          *,
          sections:community_page_sections(*)
        `)
        .eq('community_id', currentCommunity.id)
        .eq('page_type', 'privacy')
        .eq('is_published', true)
        .single();

      if (error) {
        console.error('Error fetching community privacy page:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Privacy Policy page not found');
      }

      // Sort sections by display_order
      const sortedSections = (data.sections || [])
        .filter((section) => section.is_visible)
        .sort((a, b) => a.display_order - b.display_order);

      return {
        ...data,
        sections: sortedSections
      } as CommunityPageWithSections;
    },
    enabled: !!currentCommunity?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Set document title and meta description
  React.useEffect(() => {
    if (pageData?.meta_description) {
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', pageData.meta_description);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = pageData.meta_description;
        document.head.appendChild(meta);
      }
    }
  }, [pageData?.meta_description]);

  // Loading state
  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen">
          {/* Header skeleton */}
          <div className="py-16 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <Skeleton className="h-12 w-96 mx-auto mb-6" />
              <Skeleton className="h-6 w-80 mx-auto" />
            </div>
          </div>

          {/* Content skeletons */}
          <div className="max-w-4xl mx-auto px-4 pb-16 space-y-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center max-w-md mx-auto">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Page Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The Privacy Policy page for this community could not be loaded.
            </p>
            <p className="text-sm text-muted-foreground">
              {error.message}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  // No data state
  if (!pageData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center max-w-md mx-auto">
            <h1 className="text-2xl font-bold mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground">
              The Privacy Policy page for this community is not yet configured.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <FadeIn>
        <div className="min-h-screen">
          {/* Render sections */}
          {pageData.sections && pageData.sections.length > 0 ? (
            pageData.sections.map((section) => (
              <SectionRenderer 
                key={section.id} 
                section={section}
              />
            ))
          ) : (
            // Fallback content
            <div className="min-h-screen flex items-center justify-center px-4">
              <div className="text-center max-w-2xl mx-auto">
                <h1 className="text-2xl lg:text-3xl font-bold mb-6">
                  Privacy Policy
                </h1>
                <p className="text-xl text-muted-foreground">
                  Privacy Policy content is being configured for {currentCommunity?.name}.
                </p>
              </div>
            </div>
          )}
        </div>
      </FadeIn>
    </Layout>
  );
};

export default CommunityPrivacyPage;